import { useMemo, useRef, type RefObject } from 'react';
import { theme } from 'antd';
import DataTableEcho from '@/components/tables/DataTable/DataTableEcho';
import { buildShowPerformanceEchoColumns } from '@/components/tables/buildShowPerformanceEchoColumns';
import { uppercaseFirstLetter } from '@/utils/stringUtils';
import { formatPercentage } from '@/utils/numberFormattingUtils';
import type { NetworkShow } from '@/types/api/network';
import type { PageType } from '@/types/api';
import type { TableData, TableHeader } from '@/types';
import useBreakpoint from '@/hooks/useBreakpoint';
import NoDataMessage from '@/components/states/NoDataMessage';
import { TotalImpressionsTooltipEcho } from '@/components/tooltips/ImpressionsTooltipsEcho';

const REVENUE_KEYS = [
  'ads',
  'programmatic',
  'sponsorships',
  'branded_content',
  'social',
  'other',
] as const;

const buildShowPerformanceHeaders = (
  popoverRef: RefObject<HTMLDivElement>,
  pageType: PageType,
  isMobile: boolean,
  isTablet: boolean
): TableHeader[] => {
  const nameMinWidth = isMobile ? 175 : isTablet ? 200 : 250;

  return [
    {
      id: 'name',
      label: 'Show name',
      sortable: true,
      styleOptions: { minWidth: nameMinWidth },
    },
    {
      id: 'impressions',
      label: <TotalImpressionsTooltipEcho popoverRef={popoverRef} />,
      sortable: true,
      styleOptions: { align: 'right', minWidth: 125 },
    },
    ...REVENUE_KEYS.map((key) => ({
      id: key,
      label: uppercaseFirstLetter(key.split('_')[0]),
      sortable: true,
      styleOptions: { align: 'right' as const, minWidth: 130 },
    })),
    {
      id: 'total',
      label: pageType === 'payouts' ? 'Total revenue' : 'Total est. earnings',
      sortable: true,
      styleOptions: { align: 'right', minWidth: 150 },
    },
    {
      id: 'percentage',
      label: '% of total',
      styleOptions: { align: 'right', minWidth: 100 },
    },
  ];
};

const showToSortableTableData = (show: NetworkShow): TableData => ({
  id: show.id,
  columns: [
    { id: 'name', value: show.name },
    {
      id: 'impressions',
      type: 'number',
      value: String(Number(show.impressions) || 0),
    },
    ...REVENUE_KEYS.map((key) => ({
      id: key,
      type: 'number' as const,
      value: String(show.revenue[key].amount),
    })),
    {
      id: 'total',
      type: 'number',
      value: String(show.revenue.total.amount),
    },
    {
      id: 'percentage',
      type: 'percentage',
      value: `${formatPercentage(show.percentage, { round: false, decimals: 2 })}%`,
    },
  ],
});

export const ShowPerformanceTableEcho = ({
  data,
  isLoading = false,
  type = 'earnings',
}: {
  data: NetworkShow[] | undefined;
  isLoading?: boolean;
  type?: PageType;
}) => {
  const { token } = theme.useToken();
  const { isMobile, isTablet } = useBreakpoint();
  const popoverRef = useRef<HTMLDivElement>(null);

  const headerData = useMemo(
    () => buildShowPerformanceHeaders(popoverRef, type, isMobile, isTablet),
    [type, isMobile, isTablet]
  );

  const tableData = useMemo(() => (data ?? []).map(showToSortableTableData), [data]);

  const showById = useMemo(
    () => new Map((data ?? []).map((showItem) => [showItem.id, showItem])),
    [data]
  );

  const columns = useMemo(
    () => buildShowPerformanceEchoColumns(headerData, showById, token),
    [headerData, showById, token]
  );

  if (!isLoading && !data) {
    return <NoDataMessage dataType={type} />;
  }

  return (
    <DataTableEcho
      dataTestId="shows"
      columns={columns}
      dataSource={tableData}
      isLoading={isLoading}
      tableProps={{ scroll: { x: true } }}
      pagination={{
        summary: true,
        pageSizeOptions: [5, 10, 15, 20],
        defaultPageSize: type === 'payouts' ? 5 : 10,
      }}
    />
  );
};
